import React, { useEffect, useState, useCallback } from 'react';
import io from 'socket.io-client';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Avatar,
  Badge,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Divider,
  Rating,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Pending,
  DirectionsCar,
  HomeWork,
  Construction,
  Computer,
  CleanHands,
  Gavel,
  Restaurant,
  LocalOffer,
  Chat,
  Send,
  Info,
} from '@mui/icons-material';

// ✅ Connect to backend socket
const socket = io(process.env.REACT_APP_API_URL);

// 🧠 Utility functions for restriction
const wordsToNumbers = (text) => {
  const map = {
    zero: '0', one: '1', two: '2', three: '3', four: '4',
    five: '5', six: '6', seven: '7', eight: '8', nine: '9',
  };
  return text.replace(/\b(zero|one|two|three|four|five|six|seven|eight|nine)\b/gi, (m) => map[m.toLowerCase()]);
};

const detectPhoneNumbers = (text) => {
  const converted = wordsToNumbers(text);
  return /(\+?\d{1,3})?[-.\s()]?\d{3,15}/g.test(converted);
};

const detectEmails = (text) => {
  const cleaned = text
    .replace(/\s+/g, '')
    .replace(/\[at\]|\(at\)/gi, '@')
    .replace(/\s*@\s*/g, '@')
    .replace(/\s*\.\s*/g, '.');
  return /[a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+/.test(cleaned);
};

const SPAM_WORDS = [
  'buy now', 'discount', 'cheap', 'offer', 'limited time',
  'click here', 'make money', 'earn cash', 'investment',
  'prize', 'winner', 'guaranteed', 'free trial', 'urgent',
];

// 🧩 Dealer Dashboard
const DealerDashboard = ({ user }) => {
  const [enquiries, setEnquiries] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  const [openChat, setOpenChat] = useState(false);
  const [activeInquiry, setActiveInquiry] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [openDetails, setOpenDetails] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'warning' });

  // Restriction handler
  const hasRestrictedContent = useCallback((text) => {
    if (detectEmails(text)) return '⚠️ Message contains email';
    if (detectPhoneNumbers(text)) return '⚠️ Message contains phone number';
    if (SPAM_WORDS.some((w) => text.toLowerCase().includes(w))) return '⚠️ Message contains restricted terms';
    return null;
  }, []);

  const formatTime = (ts) => {
    const date = new Date(ts);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // 🧾 Fetch dealer’s enquiries
  useEffect(() => {
    const fetchEnquiries = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/inquiries/dealer-by-email/${encodeURIComponent(user.email)}`
        );
        setEnquiries(res.data);
      } catch (err) {
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };
    if (user?.email) fetchEnquiries();
  }, [user]);

  // 🟢 Open Chat
  const handleOpenChat = async (inquiry) => {
    setActiveInquiry(inquiry);
    setOpenChat(true);
    socket.emit('joinInquiry', inquiry._id);

    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/inquiries/${inquiry._id}/messages`);
      setMessages(res.data || []);
    } catch (err) {
      console.error('Chat load error:', err);
    }
  };

  // 🟣 Open Details
  const handleOpenDetails = (inquiry) => {
    setActiveInquiry(inquiry);
    setOpenDetails(true);
  };

  // 🟡 Send Message
  const sendMessage = async () => {
    if (!chatInput.trim()) return;

    const restriction = hasRestrictedContent(chatInput);
    if (restriction) {
      setSnackbar({ open: true, message: restriction, severity: 'warning' });
      return;
    }

    const msg = {
      senderId: user.id,
      senderType: 'dealer',
      content: chatInput,
      timestamp: new Date(),
    };

    setChatInput('');
    socket.emit('sendMessage', { ...msg, inquiryId: activeInquiry._id });
    await axios.post(`${process.env.REACT_APP_API_URL}/api/inquiries/${activeInquiry._id}/messages`, msg);
  };

  // 🔁 Listen for real-time messages
  useEffect(() => {
    socket.on('newMessage', (msg) => {
      if (msg.inquiryId === activeInquiry?._id) {
        setMessages((prev) => [...prev, msg]);
      }
    });
    return () => socket.off('newMessage');
  }, [activeInquiry]);

  const filtered = enquiries.filter((e) => {
    const s = e.status?.toLowerCase();
    const match =
      e.service?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      e.name?.toLowerCase().includes(searchTerm.toLowerCase());
    if (activeTab === 1) return match && s === 'assigned';
    if (activeTab === 2) return match && s === 'completed';
    return match;
  });

  const getServiceIcon = (s) =>
    s?.includes('Real') ? <HomeWork /> :
    s?.includes('Auto') ? <DirectionsCar /> :
    s?.includes('IT') ? <Computer /> :
    s?.includes('Construction') ? <Construction /> :
    s?.includes('Clean') ? <CleanHands /> :
    s?.includes('Legal') ? <Gavel /> :
    s?.includes('Catering') ? <Restaurant /> :
    <LocalOffer />;

  const markComplete = async (id) => {
    try {
      await axios.patch(`${process.env.REACT_APP_API_URL}/api/inquiries/${id}/complete`, {
        status: 'completed',
      });
      setEnquiries((prev) => prev.map((e) => (e._id === id ? { ...e, status: 'completed' } : e)));
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  const closeSnackbar = () => setSnackbar({ ...snackbar, open: false });

  // 🧩 Render UI
  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      {/* 🌟 Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3, alignItems: 'center' }}>
        <Typography variant="h4" fontWeight={700}>Dealer Dashboard</Typography>
        <Badge badgeContent={enquiries.length} color="primary">
          <Avatar sx={{ bgcolor: '#03BAA5' }}>{user?.name?.[0]}</Avatar>
        </Badge>
      </Box>

      {/* 🧭 Tabs & Search */}
      <Paper
        sx={{
          p: 2,
          mb: 4,
          borderRadius: 3,
          background: 'linear-gradient(90deg, #03BAA5 0%, #4B698E 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
          <Tabs
            value={activeTab}
            onChange={(e, v) => setActiveTab(v)}
            textColor="inherit"
            TabIndicatorProps={{ style: { backgroundColor: 'white', height: 3 } }}
            sx={{
              '& .MuiTab-root': { color: '#fff', fontWeight: 600, textTransform: 'none' },
              '& .Mui-selected': { color: '#ffeb3b !important' },
            }}
          >
            <Tab label="All Enquiries" />
            <Tab label="Active" icon={<Pending />} iconPosition="start" />
            <Tab label="Completed" icon={<CheckCircle />} iconPosition="start" />
          </Tabs>

          <TextField
            size="small"
            placeholder="Search enquiries..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{
              bgcolor: 'white',
              borderRadius: 2,
              width: { xs: '100%', sm: 280 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search color="primary" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Paper>

      {/* 📋 Enquiries Table */}
      {loading ? (
        <Box sx={{ textAlign: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: 4 }}>
          <Table>
            <TableHead sx={{ bgcolor: '#03BAA5' }}>
              <TableRow>
                {['Service', 'Customer', 'Description', 'Date', 'Status', 'Actions', 'Chat'].map((h) => (
                  <TableCell key={h} sx={{ color: 'white', fontWeight: 600, textTransform: 'uppercase' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((e) => (
                <TableRow hover key={e._id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getServiceIcon(e.service)} {e.service}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography fontWeight={600}>{e.name}</Typography>
                    <Typography variant="body2" color="text.secondary">{e.email}</Typography>
                  </TableCell>
                  <TableCell>{e.description?.substring(0, 50) || '—'}</TableCell>
                  <TableCell>{new Date(e.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={e.status}
                      color={e.status === 'completed' ? 'success' : 'info'}
                      size="small"
                      sx={{ fontWeight: 600 }}
                    />
                  </TableCell>
                  <TableCell>
                    {e.status === 'assigned' ? (
                      <Button
                        size="small"
                        variant="contained"
                        sx={{
                          background: 'linear-gradient(90deg, #03BAA5, #4B698E)',
                          '&:hover': { background: 'linear-gradient(90deg, #4B698E, #03BAA5)' },
                        }}
                        onClick={() => markComplete(e._id)}
                      >
                        Complete
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        startIcon={<Info />}
                        onClick={() => handleOpenDetails(e)}
                      >
                        View
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button variant="outlined" color="primary" onClick={() => handleOpenChat(e)} startIcon={<Chat />}>
                      Chat
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 🧾 View Details Dialog */}
      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, #03BAA5, #4B698E)',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Enquiry Details
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
  {activeInquiry && (
    <>
      <Typography variant="h6" gutterBottom>{activeInquiry.service}</Typography>
      <Typography variant="body1" color="text.secondary" gutterBottom>
        {activeInquiry.description || 'No description provided.'}
      </Typography>
      <Divider sx={{ my: 2 }} />
      <Typography><strong>Customer Name:</strong> {activeInquiry.name}</Typography>
      <Typography><strong>Email:</strong> {activeInquiry.email}</Typography>
      <Typography><strong>Status:</strong> {activeInquiry.status}</Typography>
      <Typography><strong>Date:</strong> {new Date(activeInquiry.createdAt).toLocaleDateString()}</Typography>

      {/* ⭐ Customer Review Section */}
      {activeInquiry.status === 'completed' && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>Customer Feedback</Typography>
          {activeInquiry.rating ? (
            <Box sx={{ mb: 1 }}>
              <Rating
                value={activeInquiry.rating}
                precision={0.5}
                readOnly
                size="large"
              />
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                {activeInquiry.review || 'No written review provided.'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No review submitted yet.
            </Typography>
          )}
        </>
      )}
    </>
  )}
</DialogContent>

        <DialogActions>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* 💬 Chat Dialog */}
      <Dialog open={openChat} onClose={() => setOpenChat(false)} fullWidth maxWidth="sm">
        <DialogTitle
          sx={{
            background: 'linear-gradient(90deg, #03BAA5, #4B698E)',
            color: 'white',
            fontWeight: 600,
          }}
        >
          Chat with {activeInquiry?.name}
          <Typography variant="caption" sx={{ display: 'block', opacity: 0.8 }}>
            ⚠️ No contact info allowed
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: '#f8f9fb', height: 400, overflowY: 'auto' }}>
          {messages.map((m, i) => (
            <Box key={i} sx={{ display: 'flex', justifyContent: m.senderType === 'dealer' ? 'flex-end' : 'flex-start', my: 1 }}>
              <Box
                sx={{
                  bgcolor: m.senderType === 'dealer' ? '#03BAA5' : '#E0E0E0',
                  color: m.senderType === 'dealer' ? 'white' : 'black',
                  borderRadius: 2,
                  px: 2,
                  py: 1,
                  maxWidth: '70%',
                }}
              >
                <Typography variant="body2">{m.content}</Typography>
                <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', opacity: 0.7 }}>
                  {formatTime(m.timestamp)}
                </Typography>
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
          <TextField
            fullWidth
            value={chatInput}
            onChange={(e) => setChatInput(e.target.value)}
            placeholder="Type a message..."
            onKeyDown={(e) => e.key === 'Enter' && sendMessage()}
            size="small"
          />
          <Button onClick={sendMessage} variant="contained" endIcon={<Send />}>
            Send
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={closeSnackbar}>
        <Alert onClose={closeSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DealerDashboard;
